import { defineStore } from 'pinia'
import { ref } from 'vue'
import { z } from 'zod'
import { useAxios } from '@/composables/useAxios'
import { PricingBasis, TaxReason } from '@/types'
import { useJwtStore } from './jwt'

// The taxation fields of GET /account/vat-status, which mean the same as in
// GET /cart/{id}/totals. They are optional there, so a field that is missing
// or unknown reads as absent rather than failing the whole body.
const VatStatusPricing = z.object({
  pricing_basis: PricingBasis.optional().catch(undefined),
  tax_reason: TaxReason.optional().catch(undefined)
})

export interface AccountPricing {
  basis: PricingBasis
  /** Why the account is taxed as it is; null until the backend says. */
  reason: TaxReason | null
}

export const GROSS_PRICING: AccountPricing = { basis: 'gross', reason: null }

// A pending VAT ID check can settle in the background and change the basis,
// so an older answer is asked for again.
const MAX_AGE_MS = 5 * 60 * 1000

/**
 * The signed-in account's pricing basis, shared by every price on the page:
 * callers share one request per identity for up to five minutes, and an
 * account save asks again.
 * Gross before the answer and after a failure, which the next caller retries.
 */
export const useAccountPricingStore = defineStore('accountPricing', () => {
  const pricing = ref<AccountPricing>(GROSS_PRICING)
  let request: Promise<void> | null = null
  let identity: number | null | undefined
  let requestedAt = 0
  let generation = 0

  function load(): Promise<void> {
    const id = useJwtStore().getJwtContent?.id ?? null
    if (request && id === identity && Date.now() - requestedAt < MAX_AGE_MS) return request
    if (id !== identity) pricing.value = GROSS_PRICING
    identity = id
    requestedAt = Date.now()
    const current = ++generation
    request = useAxios<never, unknown>('GET', '/account/vat-status', true)
      .then((res) => {
        if (current !== generation) return
        const parsed = VatStatusPricing.safeParse(res.data)
        if (!parsed.success) return
        pricing.value = { basis: parsed.data.pricing_basis ?? 'gross', reason: parsed.data.tax_reason ?? null }
      })
      .catch(() => {
        if (current === generation) request = null
      })
    return request
  }

  // After a save that can change the basis. Asked again at once if anything
  // has read it, so prices already on screen follow without a remount; the
  // current answer stands until the new one arrives.
  function invalidate() {
    generation++
    request = null
    if (identity !== undefined) void load()
  }

  return { pricing, load, invalidate }
})
